import { createAction, createReducer } from '@reduxjs/toolkit';
import {
  CoreApi,
  FadeAnimationSystem,
  HideFromAssemblyInstructionsComponent,
  HideOccludingObjectsSystem,
  Product,
  ProductType,
  sortByWallPosition
} from '@inter-ikea-storageone/range';
import { ThunkDispatch } from 'redux-thunk';
import * as R from 'ramda';
import { RootState } from 'ducks';
import { CaptureImageForSingleFrameAssemblyInstructionsType } from 'contexts/screenshot';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { BestaFrameComponent } from '@inter-ikea-storageone/range-besta';
import { HierarchyUtils } from '@inter-ikea-core/core';

import { IVector3 } from '@inter-ikea-betrakta/viewer';
import { PointSeriesAssemblyInstruction } from '@inter-ikea-core/core';
import { getRange, rangeIsBesta } from 'utils/queryUtils';
import { isNAMarket } from 'i18n/i18nUtils';

export type Instruction = {
  holeNumber: number;
  position: IVector3;
  entityId: number;
  productName: string;
  description: string;
  articleNumber: string;
};

export type InstructionStep = {
  instructionStepTitleCount: number;
  productId: string;
  imageData: {
    image: string;
    points: Array<{ x: number; y: number }>;
  };
  width: number;
  productName: string;
  entityId: number;
  description: string;
  instructions: Array<Instruction>;
};

export type InstructionStepWithoutImageData = {
  instructionStepTitleCount: number;
  productId: string;
  width: number;
  productName: string;
  entityId: number;
  description: string;
  instructions: Array<Instruction>;
};

export type AssemblyInstructionsForFrameEntity = Array<{
  entityId: number;
  position: IVector3;
  instructions: PointSeriesAssemblyInstruction[];
  productRef: string;
  wallPosition: string;
}>;

export type AssemblyInstructionsState = {
  instructions: InstructionStep[];
  loaded: boolean;
  loadedInstructions: number[];
};

/**
 * Only used so that action types can be exported without
 * exporting the actual actions
 */
export const actionType = {
  prepareAssemblyInstructions: 'assemblyInstructions/prepare',
  reset: 'assassemblyInstructions/reset',
  instructionLoaded: 'assassemblyInstructions/instructionLoaded'
};

export const reset = createAction(actionType.reset);
export const instructionLoaded = createAction<number>(actionType.instructionLoaded);

// Private Actions
const prepare = createAction<InstructionStep[]>(actionType.prepareAssemblyInstructions);

const sortInstructionByHoleNumberDescending = (
  currentInstruction: Instruction | null,
  nextInstruction: Instruction | null
) => {
  if (!currentInstruction || !nextInstruction) {
    return 0;
  }
  if (currentInstruction.holeNumber > nextInstruction.holeNumber) {
    return -1;
  }
  if (currentInstruction.holeNumber < nextInstruction.holeNumber) {
    return 1;
  }
  return 0;
};

const fixHoleNumber = (entity: Entity, hole: number) => {
  // In core the holeNumber starts from 0 so we add 1 to make it as in real life
  let holeNumber = hole + 1;
  if (
    entity.hasComponent(ComponentIds.DRAWER_COMPONENT_ID) &&
    ((getRange() === 'besta' && holeNumber !== 1) ||
      (getRange() === 'platsa' && holeNumber !== 6) ||
      (getRange() === 'smastad' && (isNAMarket() || holeNumber !== 2)))
  ) {
    // Drawers in PLATSA, BESTÅ and SMÅSTAD (except for NA markets) that is not placed in the bottom of the frame (hole 1 in besta, hole 6 in platsa and hole 2 in smastad)
    // are assembled in a different way which will affect the hole number in the frame, therefore we remove 1
    holeNumber--;
  }
  return holeNumber;
};

const getDetailedInstructionsForFrame = (
  instructions: PointSeriesAssemblyInstruction[],
  products: Product[],
  hostEntityId: number
) => {
  return instructions
    .map((instruction: PointSeriesAssemblyInstruction) => {
      const productRef = (instruction as any).guestConnectionRoot.getComponent(
        ComponentIds.PRODUCT_COMPONENT_ID
      )?.productRef;

      // Hack to only filter frames and not dividers for example from the instructions list
      const isFrame = instruction.host.hasComponent(ComponentIds.FRAME_COMPONENT_ID);
      // For bestå we need to check if the parent is a frame assembly which makes us able to filter out instructions
      // that would otherwise show up for the incorrect frame when having stacked frames
      const bestaFrameAssembly = HierarchyUtils.findWithComponentInUpwardsHierarchy(
        instruction.host,
        BestaFrameComponent.id
      );

      const isDoor = instruction.guest.hasComponent(ComponentIds.DOOR_COMPONENT_ID);
      const shouldBeHiddenFromAssemblyInstructions = instruction.guest.hasComponent(
        HideFromAssemblyInstructionsComponent.id
      );

      // We use the guestConnectionRoot entity id here to get the top level entity
      // In some cases we could not find the entity (for example mesh baskets)
      const matchingProduct = products.find((p: Product) => p.coreProductId === productRef);

      const isBestaDrawer =
        rangeIsBesta() && instruction.guest.hasComponent(ComponentIds.DRAWER_COMPONENT_ID);

      const bestaDrawerFrame = products.filter(p => p.type === ProductType.BESTA_DRAWER_FRAME)[0];

      if (!matchingProduct) {
        return null;
      }

      if (
        (hostEntityId !== instruction.host.id && isFrame) ||
        (bestaFrameAssembly && hostEntityId !== bestaFrameAssembly.id) ||
        isDoor ||
        shouldBeHiddenFromAssemblyInstructions
      ) {
        return null;
      }

      return {
        articleNumber: matchingProduct.ruItemNo,
        productName: isBestaDrawer ? bestaDrawerFrame.name : matchingProduct.name,
        position: {
          x: instruction.holePositionWorldSpace.X,
          y: instruction.holePositionWorldSpace.Y,
          z: instruction.holePositionWorldSpace.Z
        },
        hostId: instruction.host.id,
        entityId: instruction.guest.id,
        holeNumber: fixHoleNumber(instruction.guest, instruction.holeNumber),
        description: isBestaDrawer ? bestaDrawerFrame.typeName : matchingProduct.typeName
      };
    })
    .filter(Boolean)
    .sort(sortInstructionByHoleNumberDescending);
};

const transformAssemblyInstructionsForFrameEntity = (
  products: Product[],
  assemblyInstructionsForFrames: AssemblyInstructionsForFrameEntity,
  frameSortOrder: 'ltr' | 'rtl'
) => {
  // Used only for the title numbering
  let frameCount = 0;

  const sortedByWallPosition: AssemblyInstructionsForFrameEntity = sortByWallPosition(
    assemblyInstructionsForFrames,
    frameSortOrder
  );

  return sortedByWallPosition
    .map(assemblyInstructionForFrame => {
      const hostProduct: Product | undefined = products.find(
        (p: Product) => p.coreProductId === assemblyInstructionForFrame.productRef
      );

      if (hostProduct) {
        const detailedInstructions = getDetailedInstructionsForFrame(
          assemblyInstructionForFrame.instructions,
          products,
          assemblyInstructionForFrame.entityId
        );

        frameCount++;
        return {
          instructionStepTitleCount: frameCount,
          productId: hostProduct.ruItemNo,
          width: hostProduct.communicatedDimensions.width,
          productName: hostProduct.name,
          entityId: assemblyInstructionForFrame.entityId,
          description: hostProduct.typeName,
          position: {
            X: assemblyInstructionForFrame.position.x,
            y: assemblyInstructionForFrame.position.y,
            z: assemblyInstructionForFrame.position.z
          },
          instructions: detailedInstructions && detailedInstructions
        };
      }

      return null;
    })
    .filter(Boolean);
};

const addImageToAssemblyInstruction = async (
  instructionStep: InstructionStepWithoutImageData,
  coreApi: CoreApi,
  captureImageForSingleFrameAssemblyInstructions: CaptureImageForSingleFrameAssemblyInstructionsType
) => {
  const holes = instructionStep.instructions.map(instruction => {
    return {
      position: instruction.position,
      id: instruction.entityId,
      holeNumber: instruction.holeNumber
    };
  });
  const assemblyInstructionImageDataForSingleFrame = await captureImageForSingleFrameAssemblyInstructions(
    instructionStep.entityId,
    holes,
    coreApi.ipexViewer
  );

  const sortByPositionY = R.sortBy(R.prop('y'));

  const sortedHolePoints = sortByPositionY(
    assemblyInstructionImageDataForSingleFrame.points
  ).reverse();

  return {
    image: assemblyInstructionImageDataForSingleFrame.image,
    points: sortedHolePoints
  };
};

// Public Actions
export const prepareAssemblyInstructions = (
  productsInScene: Product[],
  assemblyInstructions: AssemblyInstructionsForFrameEntity,
  coreApi: CoreApi,
  captureImageForSingleFrameAssemblyInstructions: CaptureImageForSingleFrameAssemblyInstructionsType,
  restoreVisuals: () => void,
  frameSortOrder: 'ltr' | 'rtl'
) => async (dispatch: ThunkDispatch<any, any, any>) => {
  const productInstructionsList = transformAssemblyInstructionsForFrameEntity(
    productsInScene,
    assemblyInstructions,
    frameSortOrder
  ) as InstructionStepWithoutImageData[];

  const addImageToInstructions = async (instructions: InstructionStepWithoutImageData[]) => {
    const assemblyInstructionWithImages = [];

    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.disable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.disable();

    for (let i = 0; i < instructions.length; i++) {
      const image = await addImageToAssemblyInstruction(
        instructions[i],
        coreApi,
        captureImageForSingleFrameAssemblyInstructions
      );

      assemblyInstructionWithImages.push({ ...instructions[i], imageData: image });
    }

    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.enable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.enable();

    return assemblyInstructionWithImages;
  };

  coreApi.lights?.setShadowsEnabled(false);
  addImageToInstructions(productInstructionsList).then(instructionStepsWithImages => {
    coreApi.lights?.setShadowsEnabled(true);
    dispatch(prepare(instructionStepsWithImages));
    restoreVisuals();
  });
};

// Reducer
export const initialState: AssemblyInstructionsState = {
  instructions: [],
  loaded: false,
  loadedInstructions: []
};

const assemblyInstructionsReducer = createReducer(initialState, builder => {
  builder
    .addCase(prepare, (state, action) => {
      state.instructions = action.payload;
    })
    .addCase(reset, state => {
      state.instructions = [];
      state.loaded = false;
      state.loadedInstructions = [];
    })
    .addCase(instructionLoaded, (state, action) => {
      const id = action.payload;
      if (!state.loadedInstructions.includes(id) && !state.loaded) {
        state.loadedInstructions.push(id);
      }
      if (state.loadedInstructions.length === state.instructions.length) {
        state.loaded = true;
      }
    });
});

// Selectors
export const getAssemblyInstructions = (state: RootState) =>
  state.assemblyInstructions.instructions;

export const assemblyInstructionsLoaded = (state: RootState) =>
  state.assemblyInstructions.instructions.length === 0 || state.assemblyInstructions.loaded;

export default assemblyInstructionsReducer;
